/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { setLastSelectedBranchId } from '../../shared/localPrefs';
import { navigateRoot } from '../../shell/navigation/rootNavigation';
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { listBranchesWithOpenCashSession } from '../../cash/infra-api';

type Props = {
  navigation: NavigationProp<ParamListBase>;
};

export function EmployeeBranchPickerHub({ navigation }: Props) {
  const allBranches = useSessionStore((s) => s.branches);
  const branches = useMemo(
    () => allBranches.filter((b) => b.isActive !== false),
    [allBranches],
  );
  const setCurrentBranch = useSessionStore((s) => s.setCurrentBranch);
  const [pickedBranchId, setPickedBranchId] = useState<string | null>(null);
  const [cashByBranch, setCashByBranch] = useState<Record<string, boolean>>({});
  const [loadingCash, setLoadingCash] = useState(true);

  const sortedBranches = useMemo(
    () => [...branches].sort((a, b) => a.name.localeCompare(b.name)),
    [branches],
  );

  const reloadCash = useCallback(async () => {
    setLoadingCash(true);
    try {
      // #1296: una sola RPC batch en vez de N getMergedActiveCashSessionId.
      // null = desconocido → las filas quedan en "Caja —" (no "cerrada").
      const open = await listBranchesWithOpenCashSession(
        sortedBranches.map((branch) => branch.id),
      );
      if (open != null) {
        setCashByBranch(
          Object.fromEntries(sortedBranches.map((branch) => [branch.id, open.has(branch.id)])),
        );
      }
    } finally {
      setLoadingCash(false);
    }
  }, [sortedBranches]);

  useEffect(() => {
    void reloadCash();
  }, [reloadCash]);

  const { refreshing, onRefresh } = useScreenRefresh(reloadCash);

  const enterBranch = useCallback(async () => {
    if (!pickedBranchId) return;
    setCurrentBranch(pickedBranchId);
    await setLastSelectedBranchId(pickedBranchId);
    navigateRoot(navigation, 'AppShell');
  }, [navigation, pickedBranchId, setCurrentBranch]);

  return (
    <ShellContentFrame>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        testID="post-login-hub.picker"
      >
        <Text style={styles.kicker}>Hub · empleado</Text>
        <Text style={styles.title}>¿Dónde trabajás hoy?</Text>
        <Text style={styles.subtitle}>Elegí sucursal para POS y permisos</Text>

        {loadingCash && sortedBranches.length === 0 ? (
          <ActivityIndicator color={ticketColors.gold} style={styles.loader} />
        ) : null}

        <View style={styles.list}>
          {sortedBranches.map((branch) => {
            const selected = pickedBranchId === branch.id;
            const cashOpen = cashByBranch[branch.id];
            return (
              <TouchableOpacity
                key={branch.id}
                style={[styles.row, selected && styles.rowSelected]}
                onPress={() => setPickedBranchId(branch.id)}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                accessibilityLabel={`Elegir ${branch.name}`}
                testID={`post-login-hub.picker.${branch.id}`}
              >
                <View style={[styles.radio, selected && styles.radioSelected]}>
                  {selected ? <View style={styles.radioDot} /> : null}
                </View>
                <View style={styles.rowCopy}>
                  <Text style={styles.rowTitle}>{branch.name}</Text>
                  <Text style={styles.rowMeta}>
                    {cashOpen == null ? 'Caja —' : cashOpen ? 'Caja abierta' : 'Caja cerrada'}
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
              </TouchableOpacity>
            );
          })}
        </View>

        <TouchableOpacity
          style={[styles.cta, !pickedBranchId && styles.ctaDisabled]}
          onPress={() => void enterBranch()}
          disabled={!pickedBranchId}
          accessibilityRole="button"
          accessibilityLabel="Entrar al local"
          testID="post-login-hub.enter-branch"
        >
          <Text style={styles.ctaText}>Entrar al local</Text>
        </TouchableOpacity>
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  scroll: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  kicker: {
    fontFamily: ticketFonts.sans,
    fontWeight: '500',
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
  },
  title: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 22,
    color: ticketColors.ink,
  },
  subtitle: {
    fontFamily: ticketFonts.sans,
    fontSize: 14,
    color: ticketColors.ink2,
    marginTop: -4,
  },
  loader: { marginTop: spacing.xl },
  list: { gap: spacing.sm },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.md,
  },
  rowSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: 'rgba(180, 83, 9, 0.06)',
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: ticketColors.gold,
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ticketColors.gold,
  },
  rowCopy: { flex: 1, gap: 2 },
  rowTitle: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 15,
    color: ticketColors.ink,
  },
  rowMeta: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink2,
  },
  cta: {
    marginTop: spacing.sm,
    backgroundColor: ticketColors.gold,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  ctaDisabled: {
    opacity: 0.45,
  },
  ctaText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '700',
    fontSize: 15,
    color: ticketColors.paper,
  },
});
